/**
 * refMarkers — parse `[ref:kind:id]` inline markers emitted by the Writing
 * Agent and resolve them against the pinned reference chips snapshotted on
 * the Brief at intake, plus a workspace-wide fallback index of docs /
 * folders / buckets.
 *
 * The Draft Canvas registers a lightweight ProseMirror decoration extension
 * (`RefMarkerDecoration`) that reads from both registries and rebuilds
 * decorations whenever either updates.
 */

export type RefKind = "excerpts" | "library" | "corpus" | "notes" | "podcasts";

export type ResolvedRefChip = {
  kind: RefKind;
  id: string;
  label: string;
  usage?: "cite" | "consult" | "quote_verbatim";
  body_excerpt?: string | null;
  sublabel?: string | null;
};

/** Matches `[ref:kind:id]` — kind is snake/lower, id is uuid-ish or prefixed. */
export const REF_MARKER_RE = /\[ref:([a-z_]+):([a-zA-Z0-9:_-]+)\]/g;

export type RefToken =
  | { type: "text"; value: string }
  | { type: "ref"; kind: RefKind; id: string; marker: string };

const KNOWN_KINDS: ReadonlySet<string> = new Set([
  "excerpts",
  "library",
  "corpus",
  "notes",
  "podcasts",
]);

/** Split a raw string into text + ref tokens. Unknown kinds fall through as text. */
export const tokenizeRefMarkers = (input: string): RefToken[] => {
  if (!input) return [];
  const out: RefToken[] = [];
  let last = 0;
  const re = new RegExp(REF_MARKER_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) {
    if (!KNOWN_KINDS.has(m[1])) continue;
    if (m.index > last) out.push({ type: "text", value: input.slice(last, m.index) });
    out.push({ type: "ref", kind: m[1] as RefKind, id: m[2], marker: m[0] });
    last = re.lastIndex;
  }
  if (last < input.length) out.push({ type: "text", value: input.slice(last) });
  return out;
};

export const resolveChip = (
  kind: string,
  id: string,
  chips: ResolvedRefChip[] | null | undefined,
): ResolvedRefChip | null => {
  if (!chips || chips.length === 0) return null;
  return chips.find((c) => c.kind === kind && c.id === id) ?? null;
};

// ---------- Live registries ----------
// - piece registry: per-piece pinned intake_attachments (has usage/excerpt)
// - workspace registry: id-indexed lookup for docs/folders/buckets so drafts
//   can resolve markers that came from grounding retrieval (not pinning).

const registry = new Map<string, ResolvedRefChip[]>();
const listeners = new Set<() => void>();

export const setChipRegistry = (pieceId: string | null, chips: ResolvedRefChip[]) => {
  if (!pieceId) return;
  registry.set(pieceId, chips);
  notify();
};

export const getChipRegistry = (pieceId: string | null): ResolvedRefChip[] => {
  if (!pieceId) return [];
  return registry.get(pieceId) ?? [];
};

export const subscribeChipRegistry = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

// ---------- Workspace-scoped fallback index ----------

export type WorkspaceChip = {
  /** UI kind — doc/folder/bucket for corpus surfaces; also mirrors RefKind for library/etc. */
  kind: "doc" | "folder" | "bucket" | RefKind;
  id: string;
  label: string;
};

/** kind → id → chip. Kept as an object so callers can shallow-swap entries. */
type WorkspaceIndex = Record<string, Map<string, WorkspaceChip>>;
let workspaceIndex: WorkspaceIndex = {};

const notify = () => {
  listeners.forEach((l) => {
    try { l(); } catch { /* noop */ }
  });
};

export const setWorkspaceChipIndex = (chips: WorkspaceChip[]) => {
  const next: WorkspaceIndex = {};
  for (const c of chips) {
    const bucket = next[c.kind] ?? (next[c.kind] = new Map());
    bucket.set(c.id, c);
  }
  workspaceIndex = next;
  notify();
};

/** Resolve `[ref:kind:id]` against the workspace-wide index.
 *  Handles kind aliases (corpus/doc/document → doc). */
export const resolveWorkspaceChip = (kind: string, id: string): WorkspaceChip | null => {
  const k = kind.toLowerCase();
  const aliases: string[] =
    k === "corpus" || k === "document" ? ["doc", "corpus"]
    : k === "doc" ? ["doc", "corpus"]
    : [k];
  for (const a of aliases) {
    const hit = workspaceIndex[a]?.get(id);
    if (hit) return hit;
  }
  return null;
};

const readString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const readNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * Build per-draft Corpus chips from `content_piece_drafts.context_snapshot`.
 *
 * New drafts carry `corpus_citation_aliases`, which maps legacy numeric row
 * aliases back to the real document title. Older drafts only have the per-doc
 * rollup (`corpus_document_titles`); because that rollup preserves retrieval
 * order and chunk counts, we can reconstruct `[ref:corpus:N]` aliases well
 * enough to label existing numeric chips as document chips.
 */
export const buildCorpusRefChipsFromSnapshot = (contextSnapshot: unknown): ResolvedRefChip[] => {
  if (!contextSnapshot || typeof contextSnapshot !== "object" || Array.isArray(contextSnapshot)) {
    return [];
  }

  const snap = contextSnapshot as Record<string, unknown>;
  const corpusTitles = Array.isArray(snap.corpus_document_titles)
    ? snap.corpus_document_titles as Array<Record<string, unknown>>
    : [];
  const corpusAliases = Array.isArray(snap.corpus_citation_aliases)
    ? snap.corpus_citation_aliases as Array<Record<string, unknown>>
    : [];

  const docChips = corpusTitles.flatMap((r): ResolvedRefChip[] => {
    const id = readString(r?.id);
    const title = readString(r?.title);
    if (!id || !title) return [];
    return [{ kind: "corpus", id, label: title, usage: "consult" }];
  });

  const explicitAliasChips = corpusAliases.flatMap((r): ResolvedRefChip[] => {
    const alias = readString(r?.alias);
    const title = readString(r?.title);
    const documentId = readString(r?.document_id);
    if (!alias || !title) return [];
    return [{
      kind: "corpus",
      id: alias,
      label: title,
      sublabel: documentId,
      usage: "consult",
    }];
  });

  if (explicitAliasChips.length > 0) return [...docChips, ...explicitAliasChips];

  let cursor = 1;
  const inferredAliasChips = corpusTitles.flatMap((r): ResolvedRefChip[] => {
    const title = readString(r?.title);
    const documentId = readString(r?.id);
    const count = readNumber(r?.chunk_count);
    if (!title || !documentId || count === null) return [];
    const chips = Array.from({ length: Math.max(0, count) }, (_, i): ResolvedRefChip => ({
      kind: "corpus",
      id: String(cursor + i),
      label: title,
      sublabel: documentId,
      usage: "consult",
    }));
    cursor += Math.max(0, count);
    return chips;
  });

  return [...docChips, ...inferredAliasChips];
};
